import Big from 'big.js';
import _ from 'lodash';

import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import {
  Price,
  SubscriptionGeneralTabDocument,
  useBeginCheckoutSessionMutation,
  useCancelSubscriptionPlanMutation,
  useSubscriptionGeneralTabQuery
} from 'generated/graphql';
import {
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';

import { Close } from '@mui/icons-material';
import { CheckoutSessionDrawer } from 'modules/components/layer33';
import { ProductsSection } from 'modules/components/layer35';
import {
  useEffect,
  useMemo,
  useState
} from 'react';
import { theme } from 'theme';

import imgUserAdd from 'assets/userAdd.svg';
import { ManageSeatCountModal } from 'modules/components/layer15';



export const FC = () => {
  const location = useLocation();
  const [
    manageSeatCountModalVisible,
    setManageSeatCountModalVisible
  ] = useState(!_.isNil(location.state?.openManageSeatsModal) ? location.state.openManageSeatsModal : false);
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.openManageSeatsModal) {
      navigate(location.pathname, {
        state: undefined
      });
    }
  }, [
    location.pathname,
    location.state,
    navigate
  ]);

  const { organizationId } = useParams<{ organizationId: string }>();
  const { data } = useSubscriptionGeneralTabQuery({
    skip: !organizationId,
    variables: {
      organizationId: organizationId ?? ''
    }
  });

  const [cancelSubscription] = useCancelSubscriptionPlanMutation({
    variables: {
      input: {
        cancellationDetails: '',
        organizationId: organizationId || ''
      }
    },
    refetchQueries: [{
      query: SubscriptionGeneralTabDocument,
      variables: {
        organizationId
      }
    }]
  });
  const [beginCheckoutSession] = useBeginCheckoutSessionMutation();

  const [
    selectedPrice,
    setSelectedPrice
  ] = useState<Price | null>(null);
  const [
    clientSecret,
    setClientSecret
  ] = useState('');

  const handleClickSelectPrice = async (price: Price) => {
    if (!organizationId) {
      window.alert('Unable to begin checkout session. Some error finding the organization.');
      return;
    }

    const response = await beginCheckoutSession({
      variables: {
        organizationId,
        priceId: price.id
      }
    });

    const session = response.data?.beginCheckoutSession;
    if (!session) {
      window.alert('Some error beginning the checkout session.');
      setClientSecret('');
      return;
    }

    setSelectedPrice(price);
    setClientSecret(session.clientSecret);
  };



  const handleClickCancel = () => {
    if (organizationId) {
      cancelSubscription({
        variables: {
          input: {
            cancellationDetails: window.prompt('Why are you cancelling?') ?? 'N/A',
            organizationId
          }
        }
      });
    }
  };



  const totalSeatPrice = useMemo(() => {
    let totalSeats = data?.organization?.memberships.length ?? 1;
    return selectedPrice?.tiers.reduce((sum, curr) => {
      if (curr.upTo) {
        const chunk = totalSeats - curr.upTo;
        if (chunk <= 0) {
          const amt = sum.add(curr.unitAmount.mul(totalSeats));
          totalSeats -= curr.upTo;
          return amt;
        }

        return sum.add(curr.unitAmount.mul(chunk));
      }

      return sum.add(curr.unitAmount.mul(totalSeats));
    }, Big(0)) ?? Big(0);
  }, [
    data?.organization?.memberships.length,
    selectedPrice?.tiers
  ]);



  if (data?.subscriptionPlan) {
    return (
      <>


        {manageSeatCountModalVisible && organizationId && (
          <ManageSeatCountModal.FC
            organizationId={organizationId}
            onClose={() => setManageSeatCountModalVisible(false)}
          />
        )}
        <Box
          display='flex'
          flexDirection='column'
          rowGap='2.5rem'
          sx={{
            paddingTop: '2.75rem',
            mx: '2.5rem',
            my: '1.5rem'
          }}
        >
          <Box>
            <Typography variant='formLabel'>
              Current Subscription plan
            </Typography>

            <Stack
              direction='row'
              justifyContent='space-between'
              borderRadius='.5rem'
              columnGap='1rem'
              padding='1rem'
              border={`1px solid ${theme.palette.gray.light20}`}
            >
              <Box
                bgcolor={theme.palette.primary.main}
                borderRadius='.5rem'
                height='2.5rem'
                width='2.5rem'
                alignContent='center'
              >
                <Typography
                  variant='h4'
                  textAlign='center'
                  color='#FFF'
                >
                  {data.subscriptionPlan.description}
                </Typography>
              </Box>
              <Box
                flex='1 1 0'
              >
                <Typography
                  variant='h4Heavy'
                  color={theme.palette.primary.main}
                  textTransform='uppercase'
                >
                  {data.subscriptionPlan.description}
                </Typography>
                <Stack
                  direction='row'
                  columnGap='.5rem'
                >
                  <Typography
                    variant='body2'
                    paddingRight='.5rem'
                    borderRight={`1px solid ${theme.palette.gray.light80}`}
                  >
                    ${data.subscriptionPlan.latestInvoice?.amount.toFixed(2)}/month
                  </Typography>
                  <Typography
                    variant='body2'
                    color={theme.palette.gray.light60}
                  >
                    Seats: {data.subscriptionPlan.seatCount}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* Seats */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end'
          }}
          >
            <Stack gap={1}>
              <Typography variant='formLabel'>
                Available seats
              </Typography>

              <Stack
                direction='row'
                gap={2}
                divider={<Divider orientation='vertical' />}
              >
                <Typography>Total: {data.subscriptionPlan.seatCount}</Typography>

                <Typography>Active: {data.organization?.memberships.length}</Typography>

                <Typography>Free: {data.subscriptionPlan.seatCount - (data.organization?.memberships.length ?? 0)}</Typography>
              </Stack>
            </Stack>

            <Box>
              <Button
                startIcon={<img src={imgUserAdd} />}
                variant='contained'
                color='secondary'
                onClick={() => {
                  setManageSeatCountModalVisible(true);
                }}
              >
                Add
              </Button>
            </Box>
          </Box>


          {/* Payment method */}
          <Stack
            rowGap='.75rem'
            direction='column'
          >
            <Typography
              variant='formLabel'
            >
              Payment Method
            </Typography>
            <Stack
              border={`1px solid ${theme.palette.gray.light20}`}
              borderRadius='.5rem'
              padding='1rem'
              direction='row'
              columnGap='1rem'
              justifyContent='space-between'
              alignItems='center'
            >
              <Typography
                variant='tabSectionTextRegular'
              >
                {data.subscriptionPlan.description} *****
              </Typography>
              <Box flex='1 1 0'>
                <Chip
                  variant='active'
                  label={`PAID UNTIL ${data.subscriptionPlan.currentPeriodEnd.toFormat('MM/dd/yyyy')}`}
                />
              </Box>

              <Button
                disabled
                variant='outlined'
                color='secondary'
              >
                Edit
              </Button>
            </Stack>
          </Stack>

          <Stack
            gap={1}
            sx={{
              alignItems: 'flex-start'
            }}
          >
            <Typography variant='formLabel'>
              Subscription
            </Typography>

            <Typography>
              If you no longer need our service you can cancel your subscription. Please note that your plan will be canceled on your next billing period.
            </Typography>

            <Button
              color='error'
              startIcon={<Close />}
              onClick={handleClickCancel}
              disabled={!_.isNil(data.subscriptionPlan.cancellationDate)}
            >
              {data.subscriptionPlan.cancellationDate ? `Ends on ${data.subscriptionPlan.cancellationDate.toFormat('MM/dd/yyyy')}` : 'Cancel Subscription'}
            </Button>
          </Stack>
        </Box>

      </>
    );
  }

  return (
    <>
      {selectedPrice && (
        <CheckoutSessionDrawer.FC
          priceId={selectedPrice.id}
          clientSecret={clientSecret}
          amountUsd={totalSeatPrice}
          unitCount={data?.organization?.memberships.length ?? 1}
          handleClose={() => {
            setSelectedPrice(null);
            setClientSecret('');
          }}
        />
      )}

      <ProductsSection.FC
        onSelectPrice={handleClickSelectPrice}
      />
    </>
  );
};
